import React from "react";

import axios from "axios";
import clsx from "clsx";

import CircularProgress from "@busbud/design-system-components/dist/CircularProgress/CircularProgress";
import FacebookLogo from "@busbud/design-system-components/dist/Icons/Facebook/MD";
import { Button } from "@busbud/horizon";

import { useWhitelabelContext } from "@app/context/whitelabel-context";
import { toBusbudError } from "@app/helpers/api-error";
import { useAppContext } from "@app/helpers/hooks";
import { useScript } from "@app/helpers/use-script";
import type { NapiUser } from "@app/types/napi-types";

import type { ConnectButtonProps } from "../props";

import { useStyles } from "./fb-connect-button.styles";
import {
  trackClickedConnectSocial,
  trackConnectSocialError
} from "../utils/tracking";

const PROVIDER = "facebook";
const SDK_ERRORS: string[] = [];

const FACEBOOK_APP_IDS = {
  neredennereye: 491546312538938,
  default: 142556252515404
};

export const FBConnectButton: React.FC<ConnectButtonProps> = ({
  setInProgress,
  onError,
  onSuccess,
  is_sign_in = true,
  is_email_opt_in = false,
  loading = false,
  class_name,
  label
}) => {
  const { tracker, translator, geoData, user_country_code } = useAppContext();
  const { whitelabel } = useWhitelabelContext();
  const whitelabel_name = whitelabel?.name;
  const { classes } = useStyles();

  const auto_enrol_new_user =
    geoData.allowsImplicitConsentForMarketingEmails(user_country_code);

  useScript("https://connect.facebook.net/en_US/sdk.js", () => {
    const F_APP_ID =
      FACEBOOK_APP_IDS[
        (whitelabel_name as keyof typeof FACEBOOK_APP_IDS) || "default"
      ] || FACEBOOK_APP_IDS.default;
    window.FB.init({
      appId: F_APP_ID,
      cookie: true, // enable cookies to allow the server to access the session
      xfbml: true, // parse social plugins on this page
      version: "v3.2",
      status: true
    });
  });

  function handleClick() {
    setInProgress(true);
    window.FB.getLoginStatus((res: any) => {
      if (res.status !== "connected") {
        return window.FB.login(getFBUserInformation, { scope: "email" });
      }

      getFBUserInformation(res);
    });

    trackClickedConnectSocial(
      tracker,
      is_sign_in,
      PROVIDER,
      auto_enrol_new_user
    );
  }

  function getFBUserInformation(res: any) {
    const fb_token = res.authResponse && res.authResponse.accessToken;
    axios
      .post<NapiUser>("/auth/signin-fb", {
        fb_token,
        email_opt_in: is_email_opt_in,
        is_nn: whitelabel_name === "neredennereye",
        auto_enrol_new_user
      })
      .then(response => {
        setInProgress(false);
        onSuccess(response.data, PROVIDER);
      })
      .catch((err: Error | null) => {
        trackConnectSocialError(tracker, PROVIDER, SDK_ERRORS, is_sign_in, err);
        setInProgress(false);
        onError(toBusbudError(err)!);
      });
  }

  return (
    <Button
      onClick={handleClick}
      variant="contained"
      size="lg"
      className={clsx(
        class_name,
        classes.button,
        "w-full text-color-static-white-canvas hover:text-color-static-white-canvas active:text-color-static-white-canvas disabled:bg-opacity-50"
      )}
      iconStart={loading ? <CircularProgress size="sm" /> : <FacebookLogo />}
    >
      {label || translator.t("!account.authentication.facebook")}
    </Button>
  );
};
